import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import API_ENDPOINTS from "../config/api";
import DateRange from "../components/DateRange";

const Container = styled.main`
  min-height: 100dvh;
  width: 100%;
  padding: 2rem;
  display: flex;
  flex-direction: column;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #013566;
  margin: 0 0 1rem 0;
  text-align: center;
`;

const TilesSection = styled.section`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2rem;
  margin-top: 2rem;
`;

const Tile = styled.div`
  border: 2px solid #eee;
  border-radius: 12px;
  -webkit-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  -moz-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
`;

const TileHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  background: #f8f9fa;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 1rem;
  border: 1px solid #eee;

  h2 {
    font-size: 1.2rem;
    font-weight: 600;
    color: #013566;
    margin: 0;
    flex: 1;
  }
`;

const TileSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const TileRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #eee;
  gap: 1rem;
  padding: 12px 0;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
    border-radius: 4px;
    padding: 12px 8px;
    margin: 0 -8px;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TileElement = styled.div`
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  margin: 0;
  flex: 1;
  display: flex;
  align-items: center;
  word-break: break-all;
`;

const IpAddressCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const IpDropdown = styled.div`
  position: relative;
`;

const IpDropdownToggle = styled.button`
  padding: 0.35rem 0.65rem;
  border: 1px solid #013566;
  border-radius: 6px;
  background: white;
  color: #013566;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;

  &:hover {
    background: #eef3f8;
  }
`;

const IpDropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 0.35rem);
  left: 0;
  z-index: 10;
  min-width: 180px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
  overflow: hidden;
`;

const IpDropdownItem = styled.div`
  padding: 0.65rem 0.85rem;
  font-size: 0.9rem;
  color: #333;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: none;
  }
`;

const ReportButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  background-color: #198754;
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 1rem;
  align-self: flex-start;

  &:hover {
    background-color: #157347;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  width: 480px;
  max-width: 95%;
  background: #fff;
  border-radius: 12px;
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;

  h2 {
    margin: 0;
    font-size: 1.4rem;
    font-weight: 700;
    color: #013566;
  }
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #013566;
  }
`;

const ModalField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-top: 0.5rem;
`;

const ModalLabel = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: #013566;
`;

const ModalInput = styled.input`
  padding: 0.6rem 0.8rem;
  border-radius: 8px;
  border: 1px solid #ddd;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #013566;
    box-shadow: 0 0 0 2px rgba(1, 53, 102, 0.2);
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1rem;
`;

type ClickRecord = {
  id: number;
  site_id: number;
  url: string;
  ip_address: string;
  user_agent: string;
  created_at: string;
};

type SummarizedClick = {
  url: string;
  ipAddresses: string[];
  clickCount: number;
};

const summarizeClicksByUrl = (clicks: ClickRecord[]): SummarizedClick[] => {
  const urlMap = new Map<
    string,
    { clickCount: number; ipAddresses: Set<string> }
  >();

  clicks.forEach((click) => {
    const url = click.url || "N/A";
    const existing = urlMap.get(url);

    if (existing) {
      existing.clickCount += 1;
      if (click.ip_address) {
        existing.ipAddresses.add(click.ip_address);
      }
    } else {
      urlMap.set(url, {
        clickCount: 1,
        ipAddresses: new Set(click.ip_address ? [click.ip_address] : []),
      });
    }
  });

  return Array.from(urlMap.entries())
    .map(([url, { clickCount, ipAddresses }]) => ({
      url,
      clickCount,
      ipAddresses: Array.from(ipAddresses),
    }))
    .sort((a, b) => b.clickCount - a.clickCount);
};

const IpAddressDisplay = ({
  ipAddresses,
  isOpen,
  onToggle,
}: {
  ipAddresses: string[];
  isOpen: boolean;
  onToggle: () => void;
}) => {
  if (ipAddresses.length === 0) {
    return <span>N/A</span>;
  }

  const [primaryIp, ...otherIps] = ipAddresses;

  return (
    <IpAddressCell>
      <span>{primaryIp}</span>
      {otherIps.length > 0 && (
        <IpDropdown>
          <IpDropdownToggle type="button" onClick={onToggle}>
            +{otherIps.length} more {isOpen ? "▲" : "▼"}
          </IpDropdownToggle>
          {isOpen && (
            <IpDropdownMenu>
              {otherIps.map((ip) => (
                <IpDropdownItem key={ip}>{ip}</IpDropdownItem>
              ))}
            </IpDropdownMenu>
          )}
        </IpDropdown>
      )}
    </IpAddressCell>
  );
};

const formatDateParam = (date: Date | null): string => {
  if (!date) return "";
  return date.toISOString().split("T")[0];
};

const MyProtectedhomeClicks = () => {
  const token = localStorage.getItem("token");
  const [startDate, setStartDate] = useState<Date | null>(
    () => new Date(new Date().setMonth(new Date().getMonth() - 1)),
  );
  const [endDate, setEndDate] = useState<Date | null>(() => new Date());
  const [clicks, setClicks] = useState<ClickRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [openIpDropdownUrl, setOpenIpDropdownUrl] = useState<string | null>(
    null,
  );
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportStartDate, setReportStartDate] = useState("");
  const [reportEndDate, setReportEndDate] = useState("");
  const [generatingReport, setGeneratingReport] = useState(false);

  const summarizedClicks = useMemo(
    () => summarizeClicksByUrl(clicks),
    [clicks],
  );

  const fetchClicks = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      const response = await axios.get(API_ENDPOINTS.SIMPLISAFE_CLICKS, {
        params: {
          start_date: formatDateParam(startDate),
          end_date: formatDateParam(endDate),
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log(response.data);
      setClicks(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching clicks:", error);
      setClicks([]);
    } finally {
      setLoading(false);
    }
  }, [token, startDate, endDate]);

  useEffect(() => {
    fetchClicks();
  }, [fetchClicks]);

  useEffect(() => {
    setOpenIpDropdownUrl(null);
  }, [summarizedClicks]);

  const openReportModal = () => {
    setReportStartDate(formatDateParam(startDate));
    setReportEndDate(formatDateParam(endDate));
    setShowReportModal(true);
  };

  const handleGenerateReport = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reportStartDate || !reportEndDate) {
      alert("Please select a start date and end date.");
      return;
    }

    try {
      setGeneratingReport(true);
      const response = await axios.get(API_ENDPOINTS.SIMPLISAFE_CLICKS, {
        params: {
          site: 3,
          start_date: reportStartDate,
          end_date: reportEndDate,
          format: "csv",
          aggregated: "no",
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "text/csv;charset=utf-8;",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `myprotectedhome-clicks-${reportStartDate}-to-${reportEndDate}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setShowReportModal(false);
    } catch (error) {
      console.error("Error generating report:", error);
      alert("Failed to generate report. Please try again.");
    } finally {
      setGeneratingReport(false);
    }
  };

  return (
    <Container>
      <PageTitle>Myprotectedhome Clicks</PageTitle>
      <DateRange
        startDate={startDate}
        endDate={endDate}
        handleStartDateChange={setStartDate}
        handleEndDateChange={setEndDate}
      />

      <ReportButton type="button" onClick={openReportModal}>
        Generate report
      </ReportButton>

      <TilesSection>
        <Tile>
          <TileHeader>
            <h2>URL</h2>
            <h2>IP Address</h2>
            <h2>Click Count</h2>
          </TileHeader>

          <TileSection>
            {loading ? (
              <TileRow>
                <TileElement style={{ width: "100%", textAlign: "center" }}>
                  Loading clicks...
                </TileElement>
              </TileRow>
            ) : summarizedClicks.length > 0 ? (
              summarizedClicks.map((click) => (
                <TileRow key={click.url}>
                  <TileElement>{click.url}</TileElement>
                  <TileElement>
                    <IpAddressDisplay
                      ipAddresses={click.ipAddresses}
                      isOpen={openIpDropdownUrl === click.url}
                      onToggle={() =>
                        setOpenIpDropdownUrl((current) =>
                          current === click.url ? null : click.url,
                        )
                      }
                    />
                  </TileElement>
                  <TileElement>{click.clickCount}</TileElement>
                </TileRow>
              ))
            ) : (
              <TileRow>
                <TileElement style={{ width: "100%", textAlign: "center" }}>
                  No clicks found
                </TileElement>
              </TileRow>
            )}
          </TileSection>
        </Tile>
      </TilesSection>

      {showReportModal && (
        <ModalOverlay
          onClick={() => {
            if (!generatingReport) {
              setShowReportModal(false);
            }
          }}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h2>Generate Clicks Report</h2>
              <CloseButton
                type="button"
                onClick={() => !generatingReport && setShowReportModal(false)}
              >
                ×
              </CloseButton>
            </ModalHeader>
            <form onSubmit={handleGenerateReport}>
              <ModalField>
                <ModalLabel htmlFor="reportStartDate">Start date *</ModalLabel>
                <ModalInput
                  id="reportStartDate"
                  type="date"
                  value={reportStartDate}
                  onChange={(e) => setReportStartDate(e.target.value)}
                  required
                />
              </ModalField>
              <ModalField>
                <ModalLabel htmlFor="reportEndDate">End date *</ModalLabel>
                <ModalInput
                  id="reportEndDate"
                  type="date"
                  value={reportEndDate}
                  onChange={(e) => setReportEndDate(e.target.value)}
                  required
                />
              </ModalField>
              <ModalActions>
                <ReportButton type="submit" disabled={generatingReport}>
                  {generatingReport ? "Generating..." : "Generate CSV"}
                </ReportButton>
              </ModalActions>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default MyProtectedhomeClicks;
